import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useOrderConfirmation } from '../api/queries.js';
import type { OrderStatus } from '../api/types.js';
import { useCart } from '../cart/cart-context.js';
import { ErrorState } from '../components/error-state.js';
import { PageHeader } from '../components/page-header.js';
import { OrderLotLinks } from '../components/search-results.js';
import { formatPrice } from '../lib/format.js';

interface Outcome {
  readonly title: string;
  readonly message: string;
  readonly icon: ReactNode;
  readonly tone: string;
}

const OUTCOMES: Readonly<Record<OrderStatus, Outcome>> = {
  PAID: {
    title: '¡Pago confirmado!',
    message: 'Tu pedido quedó registrado y está en proceso de despacho.',
    icon: <CheckCircle2 aria-hidden="true" className="size-10" />,
    tone: 'text-primary',
  },
  DISPATCHED: {
    title: 'Pedido despachado',
    message: 'Tus kits ya fueron entregados.',
    icon: <CheckCircle2 aria-hidden="true" className="size-10" />,
    tone: 'text-primary',
  },
  AWAITING_PAYMENT: {
    title: 'Confirmando tu pago…',
    message: 'Estamos consultando el resultado con la pasarela de pago.',
    icon: <Clock aria-hidden="true" className="size-10" />,
    tone: 'text-accent',
  },
  CANCELLED: {
    title: 'Compra cancelada',
    message: 'No se realizó ningún cobro. Tu carro sigue disponible.',
    icon: <XCircle aria-hidden="true" className="size-10" />,
    tone: 'text-muted-foreground',
  },
  PAYMENT_FAILED: {
    title: 'El pago falló',
    message: 'No se realizó ningún cobro. Puedes intentarlo de nuevo desde tu carro.',
    icon: <XCircle aria-hidden="true" className="size-10" />,
    tone: 'text-destructive',
  },
  EXPIRED: {
    title: 'La sesión de pago expiró',
    message: 'Pasaron más de 5 minutos sin completar el pago. Puedes intentarlo de nuevo.',
    icon: <XCircle aria-hidden="true" className="size-10" />,
    tone: 'text-destructive',
  },
};

export function CheckoutResultPage() {
  const [searchParams] = useSearchParams();
  const publicId = searchParams.get('order') ?? '';
  const order = useOrderConfirmation(publicId);
  const cart = useCart();
  const status = order.data?.status;

  useEffect(() => {
    if (status === 'PAID' || status === 'DISPATCHED') cart.clear();
  }, [status, cart]);

  if (publicId === '')
    return <PageHeader title="Resultado del pago" description="No se indicó un pedido." />;
  if (order.isError) return <ErrorState message="No encontramos tu pedido." />;
  if (order.data === undefined)
    return <PageHeader title="Resultado del pago" description="Consultando tu pedido…" />;

  const outcome = OUTCOMES[order.data.status];
  return (
    <section
      aria-live="polite"
      className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-card border border-border bg-card p-8 text-center"
    >
      <span className={outcome.tone}>{outcome.icon}</span>
      <h1 className="text-2xl font-bold">{outcome.title}</h1>
      <p className="text-muted-foreground">{outcome.message}</p>
      <dl className="w-full rounded-control bg-muted p-4 text-left">
        {order.data.lines.map((line) => (
          <div key={line.sku} className="flex justify-between py-1">
            <dt>
              {line.quantity} × {line.sku}
            </dt>
            <dd>{formatPrice(line.quantity * line.unitPrice)}</dd>
          </div>
        ))}
        <div className="mt-2 flex justify-between border-t border-border pt-2 font-semibold">
          <dt>Total</dt>
          <dd>{formatPrice(order.data.total)}</dd>
        </div>
      </dl>
      {order.data.lots.length > 0 && (
        <div className="flex w-full flex-col gap-2 text-left">
          <h2 className="font-semibold">Trazabilidad de tus kits</h2>
          <p className="text-sm text-muted-foreground">
            Cada enlace muestra de qué insumos de Farma Central se hizo ese lote. También puedes
            volver a encontrar este pedido buscando tu email en{' '}
            <Link to="/trazabilidad" className="font-medium text-accent underline">
              Trazabilidad
            </Link>
            .
          </p>
          <OrderLotLinks lots={order.data.lots} />
        </div>
      )}
      <p className="text-sm break-all text-muted-foreground">Pedido {order.data.publicId}</p>
      <Link
        to={order.data.status === 'PAID' || order.data.status === 'DISPATCHED' ? '/' : '/cart'}
        className="font-medium text-accent underline"
      >
        {order.data.status === 'PAID' || order.data.status === 'DISPATCHED'
          ? 'Volver al catálogo'
          : 'Volver al carro'}
      </Link>
    </section>
  );
}
